import { JourneyData, JourneyTarget } from '@cash-web/shared-journey-util-main'

/* Helper function to find all related journeys
 *
 * 1. Find all children of the journey
 * 2. If there are children, return them
 * 3. If there are no children, find all parents of the journey and return them
 *
 * This is used to cancel all journeys that are related to the journey that is being cancelled.
 */

export const findAllRelatedJourneys = (journeyId: string, history: JourneyData[], target?: JourneyTarget) => {
  const relatedChildren = findAllChildren(journeyId, history)
  if (relatedChildren.length > 0) {
    return relatedChildren
  }
  return findAllParents(journeyId, new Map(history.map(journey => [journey.journeyId, journey])), target)
}

const findAllParents = (journeyId: string, map: Map<string, JourneyData>, target?: JourneyTarget): string[] => {
  const instance = map.get(journeyId)
  map.delete(journeyId)
  const instanceTarget = instance?.opts?.scope?.['target'] as JourneyTarget

  if (target === undefined) {
    target = instanceTarget
  }
  const hasMatchingTarget = target === instanceTarget || instanceTarget === undefined

  const ids: string[] = hasMatchingTarget && instance != null ? [journeyId] : []

  if (instance?.parentId != null && hasMatchingTarget && instance.parentId !== journeyId) {
    ids.push(...findAllParents(instance.parentId, map, target))
  }

  return ids
}
export const findAllChildren = (journeyId: string, history: JourneyData[]): string[] => {
  const children = Object.entries(history)
    .filter(([_, journey]) => journey.journeyId !== journeyId && journey.parentId === journeyId)
    .map(([_, journey]) => journey) as JourneyData[]

  return children.reduce((acc, child) => {
    return [
      ...acc,
      child.journeyId,
      ...findAllChildren(
        child.journeyId,
        history.filter(h => h.journeyId !== child.journeyId)
      ),
    ]
  }, [] as string[])
}

export type JourneyState = {
  history: JourneyData[]
}

export type JourneyAction =
  | {
      type: 'JOURNEY_START'
      journey: JourneyData
    }
  | {
      type: 'JOURNEY_CANCEL'
      journeyId?: string
    }
  | {
      type: 'JOURNEY_COMPLETE'
      journeyId?: string
    }
  | {
      type: 'JOURNEY_CANCEL_ALL'
      excludeTargets?: JourneyTarget[]
      journeyId?: string
    }
  | {
      type: 'JOURNEY_LOCATION_CHANGE'
      location: string
    }

export const journeyPaneReducer = (state: JourneyState, action: JourneyAction): JourneyState => {
  switch (action.type) {
    case 'JOURNEY_START': {
      const currentJourney = action.journey
      const history = currentJourney.opts?.replace ? state.history.slice(0, -1) : state.history

      if (process.env['NODE_ENV'] === 'development') {
        localStorage.setItem(
          'journeys',
          JSON.stringify([
            { ...currentJourney, timeStamp: new Date() },
            ...JSON.parse(localStorage.getItem('journeys') ?? '[]').slice(0, 99),
          ])
        )
      }

      return {
        history: [...history, currentJourney],
      }
    }
    case 'JOURNEY_CANCEL':
    case 'JOURNEY_COMPLETE': {
      let history: JourneyData[]

      const journeyToRemove = action.journeyId
        ? state.history.find(h => h.journeyId === action.journeyId)
        : state.history.at(-1)

      const removedJourneyParentId = journeyToRemove?.parentId
      const removedJourneyId = journeyToRemove?.journeyId

      // Remove the journey from history
      history = action.journeyId
        ? state.history.filter(h => h.journeyId !== action.journeyId)
        : state.history.slice(0, -1)

      // Update any journeys that had the removed journey as their parent
      // to point to the removed journey's parent instead
      if (removedJourneyId) {
        history = history.map(journey => {
          if (journey.parentId === removedJourneyId) {
            return {
              ...journey,
              parentId: removedJourneyParentId,
            }
          }
          return journey
        })
      }

      return {
        history,
      }
    }
    case 'JOURNEY_CANCEL_ALL': {
      const idsToCancel = action.journeyId ? findAllRelatedJourneys(action.journeyId, state.history) : undefined
      const history = state.history.filter(h => {
        const target = h.opts?.scope?.['target'] as JourneyTarget
        const isExcludedTarget = action.excludeTargets?.includes(target)
        const notFoundInIdsToCancel = idsToCancel ? !idsToCancel.includes(h.journeyId) : false
        return isExcludedTarget || notFoundInIdsToCancel
      })

      return {
        history,
      }
    }
    case 'JOURNEY_LOCATION_CHANGE': {
      const history = state.history.filter(h => h.location === action.location || h.location === 'global')
      return {
        history,
      }
    }
    default: {
      return state
    }
  }
}
